import { SnackbarProvider, CustomContentProps, closeSnackbar } from 'notistack';
import { SonnerToast } from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

type SonnerVariant = 'positive' | 'destructive' | 'warning' | 'default';

type NotificationAction = {
  label: string;
  onClick: () => void;
};

/** Maps notistack's variant vocabulary onto the monks-ui v4 Sonner tones. */
const SONNER_VARIANT_BY_NOTISTACK: Record<string, SonnerVariant> = {
  success: 'positive',
  error: 'destructive',
  warning: 'warning',
  info: 'default',
  default: 'default',
};

function SnackbarWithRef(props: CustomContentProps & { ref?: Ref<HTMLDivElement> }) {
  const { ref } = props;
  const { t } = useTranslation('common');

  const customProps = props as CustomContentProps & {
    description?: string;
    /** Structured action forwarded by notification.service. */
    actionConfig?: NotificationAction;
  };

  return (
    // `minWidth` matches the Sonner design spec (317px). The dismiss button is
    // overlaid here, styled from global.css, because SonnerToast exposes no
    // close slot of its own.
    <div
      ref={ref}
      style={{ ...props.style, minWidth: '317px' }}
      className={`${props.className ?? ''} sonner-toast-with-dismiss`}
    >
      <SonnerToast
        variant={SONNER_VARIANT_BY_NOTISTACK[props.variant] ?? 'default'}
        title={props.message}
        description={customProps.description}
        action={customProps.actionConfig}
      />
      <button
        type="button"
        className="sonner-toast-dismiss-btn"
        aria-label={t('providers.notificationProvider.dismissNotification')}
        onClick={() => closeSnackbar(props.id)}
      >
        <XIcon size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

SnackbarWithRef.displayName = 'SnackbarWithRef';

export const StyledSnackbarProvider = ({ children }: { children: ReactNode }) => {
  return (
    <SnackbarProvider
      maxSnack={3}
      // Auto-dismiss after 6s (notistack's own default is 5s, which silently
      // undercut the 6s contract documented in notification.service). Per-toast
      // `autoHideDuration` options still override; `persist: true` disables.
      autoHideDuration={6000}
      // Bottom-right matches the Sonner / Figma default.
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      // Lift the bottom-right stack above the global UploadToast (same corner)
      // so they don't overlap. See `.notistack-toast-stack-bottom-right` in
      // global.css.
      classes={{ containerAnchorOriginBottomRight: 'notistack-toast-stack-bottom-right' }}
      Components={{
        success: SnackbarWithRef,
        error: SnackbarWithRef,
        warning: SnackbarWithRef,
        info: SnackbarWithRef,
      }}
    >
      {children}
    </SnackbarProvider>
  );
};
